import { useLocation, useNavigate } from 'react-router-dom';
import { getActiveSubNavTab } from '@/utils/moduleNavContext';

export interface SubNavTab {
  label: string;
  href: string;
  badge?: number;
}

export interface ModuleSubNavProps {
  tabs: SubNavTab[];
}

export function ModuleSubNav({ tabs }: ModuleSubNavProps) {
  const location = useLocation();
  const navigate = useNavigate();

  if (!tabs || tabs.length === 0) return null;

  // Longest-prefix wins (shared with the assistant's page derivation).
  const activeHref = getActiveSubNavTab(tabs, location.pathname)?.href ?? null;

  return (
    // Scrolls horizontally on narrow screens rather than wrapping onto a second
    // row — a wrapped sub-nav pushes the page content down and reads as two
    // separate bars. `scrollbar-none` relies on the global scrollbar reset in
    // index.css; the row is still swipeable.
    <nav
      className="hidden md:flex items-center h-11 bg-[var(--background-page-body)] border-b border-[var(--border-default)] px-4 md:px-[var(--page-gutter)] flex-shrink-0 overflow-x-auto whitespace-nowrap"
      aria-label="Module sub-navigation"
    >
      {tabs.map((tab) => {
        const isActive = tab.href === activeHref;
        return (
          <button
            key={tab.href}
            onClick={() => navigate(tab.href)}
            className={`relative flex flex-shrink-0 items-center px-3 md:px-4 h-full text-[13px] font-medium transition-colors focus:outline-none ${
              isActive
                ? 'text-[var(--color-forest)]'
                : 'text-[var(--text-content-sub-title)] hover:text-[var(--text-content-title)]'
            }`}
          >
            {tab.label}
            {tab.badge !== undefined && tab.badge > 0 && (
              <span className="ml-1.5 text-[11px] bg-[var(--color-gold-subtle)] text-[var(--color-text-gold)] border border-[var(--color-gold-border)] px-1.5 rounded-full">
                {tab.badge}
              </span>
            )}
            {isActive && (
              <span
                className="absolute bottom-0 left-0 right-0 h-0.5 bg-[var(--color-forest)]"
                aria-hidden="true"
              />
            )}
          </button>
        );
      })}
    </nav>
  );
}
